<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Homepage Popups
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openAdd()"><v-icon left>mdi-plus</v-icon>Add Popup</v-btn>
      </v-card-title>
      <v-card-text class="d-flex flex-wrap pb-0">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          style="max-width:300px"
          class="mr-6"
        ></v-text-field>
        <v-select
          v-model="eventFilter"
          :items="activeEventFilterItems"
          item-text="label"
          item-value="id"
          label="Filter by event"
          clearable
          hide-details
          style="max-width:320px"
        ></v-select>
      </v-card-text>
      <v-data-table :headers="headers" :items="filteredPopups" :items-per-page="25" :search="search" :custom-filter="filterPopups">
        <template v-slot:item.label="{ item }">
          <span>{{ item.label }}</span>
          <v-chip v-if="item.is_currently_live" x-small color="success" dark class="ml-2">
            <v-icon x-small left>mdi-eye</v-icon>Showing now
          </v-chip>
        </template>
        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 'event' ? 'primary' : ''" :outlined="item.type !== 'event'" style="max-width:260px">
            <span class="text-truncate d-inline-block">{{ item.type === 'event' ? 'Event: ' + (item.event_title || '(deleted)') : 'Custom' }}</span>
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-icon :color="item.status ? 'success' : 'grey'">{{ item.status ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
        </template>
        <template v-slot:item.window="{ item }">
          <span :title="windowTooltip(item)">{{ windowSummary(item) }}</span>
        </template>
        <template v-slot:item.stats="{ item }">
          {{ item.impressions }} shown / {{ item.clicks }} clicked
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="openEdit(item)">mdi-pencil</v-icon>
          <v-icon small class="mr-2" @click="cloneItem(item)" title="Clone with new dates">mdi-content-copy</v-icon>
          <v-icon small @click="remove(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add / Edit: two-pane editor (details left, live preview right) -->
    <v-dialog v-model="dialogOpen" fullscreen>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="dialogOpen = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>{{ dialogType === 'add' ? 'Add Popup' : 'Edit Popup' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text dark :loading="saving" @click="save()">Save</v-btn>
        </v-toolbar>

        <v-row no-gutters style="min-height: calc(100vh - 64px)">
          <!-- Left: popup details -->
          <v-col cols="12" md="6" class="pa-6" style="overflow-y:auto; max-height: calc(100vh - 64px)">
            <v-alert v-if="showLiveWarning" type="warning" dense outlined class="mb-4">
              <strong>{{ liveLabel }}</strong> is already showing on the homepage right now.
              Only one popup displays at a time — saving this one as Active will replace it
              (whichever was created or edited most recently is the one shown).
            </v-alert>

            <v-text-field v-model="form.label" label="Internal label" hint="For your own reference in this list only" persistent-hint class="mb-4"></v-text-field>

            <v-radio-group v-model="form.type" row label="Content" class="mb-2">
              <v-radio label="Link to an existing event" value="event"></v-radio>
              <v-radio label="Custom content" value="custom"></v-radio>
            </v-radio-group>

            <template v-if="form.type === 'event'">
              <v-autocomplete
                v-model="form.event_id"
                :items="eventItems"
                item-text="label"
                item-value="id"
                label="Event"
                clearable
              ></v-autocomplete>
            </template>

            <template v-else>
              <v-text-field v-model="form.custom_heading" label="Heading" class="mb-2"></v-text-field>
              <v-textarea v-model="form.custom_body" label="Body text" rows="3" class="mb-2"></v-textarea>
              <v-file-input
                v-model="customPhotoFile"
                label="Picture"
                accept="image/*"
                prepend-icon="mdi-camera"
                clearable
                show-size
                class="mb-2"
                @change="onPhotoPicked"
              ></v-file-input>
              <v-text-field v-model="form.custom_button_text" label="Button text" placeholder="Learn More" class="mb-2"></v-text-field>
              <v-text-field v-model="form.custom_link_url" label="Link URL" placeholder="https://... or /a-page-on-this-site" class="mb-2"></v-text-field>
            </template>

            <v-divider class="my-4"></v-divider>

            <v-switch v-model="form.status" label="Active"></v-switch>

            <div class="text-overline mt-2" style="color:#898781">Active Window</div>
            <div class="text-caption mb-2" style="color:#898781">
              Times are Irish time (Europe/Dublin) regardless of your own device's timezone.
            </div>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.active_from"
                  label="Active from (Irish time)"
                  type="datetime-local"
                  :rules="[v => !!v || 'Required']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.active_to"
                  label="Active to (Irish time)"
                  type="datetime-local"
                  :rules="[v => !!v || 'Required']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>

          <!-- Right: live preview -->
          <v-col cols="12" md="6" class="pa-6" style="background:#f5f5f5; max-height: calc(100vh - 64px); overflow-y:auto">
            <div class="d-flex align-center mb-4">
              <span class="text-overline" style="color:#898781">Preview</span>
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="previewDevice" mandatory dense color="primary">
                <v-btn value="web" small><v-icon left small>mdi-monitor</v-icon>Web</v-btn>
                <v-btn value="mobile" small><v-icon left small>mdi-cellphone</v-icon>Mobile</v-btn>
              </v-btn-toggle>
            </div>

            <div class="d-flex justify-center">
              <div :style="previewFrameStyle">
                <v-card :class="previewDevice === 'mobile' ? '' : 'mx-auto'" :style="previewDevice === 'web' ? 'max-width:420px' : ''">
                  <v-toolbar flat dense color="transparent">
                    <v-spacer></v-spacer>
                    <v-icon>mdi-close</v-icon>
                  </v-toolbar>
                  <v-card-text class="pt-0">
                    <v-img v-if="previewImageSrc" :src="previewImageSrc" height="220" contain style="background:#f5f5f5;border-radius:4px"></v-img>
                    <div v-else class="d-flex align-center justify-center" style="height:220px;background:#f5f5f5;border-radius:4px;border:1px dashed rgba(86,3,84,0.25)">
                      <v-icon size="56" color="primary">mdi-calendar-star</v-icon>
                    </div>
                    <div class="text-overline mt-3" style="color:#560354" v-if="previewDate">Upcoming Event</div>
                    <div class="text-h6 mb-2">{{ previewHeading || 'Popup heading' }}</div>
                    <div class="d-flex align-center mb-1" v-if="previewDate">
                      <v-icon left small>mdi-calendar</v-icon>
                      <span>{{ previewDate }}</span>
                    </div>
                    <div class="d-flex align-center mb-2" v-if="previewLocation">
                      <v-icon left small>mdi-map-marker</v-icon>
                      <span>{{ previewLocation }}</span>
                    </div>
                    <div v-if="!previewDate && previewBody" class="mt-1">{{ previewBody }}</div>
                  </v-card-text>
                  <v-card-actions class="px-4 pb-4">
                    <v-btn text>Not now</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary">{{ previewButtonText }}</v-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import Layout from '@/Shared/VuetifyAdminLayout'

export default {
  layout: Layout,
  props: {
    popups: Array,
    events: Array,
    baseurl: String,
    livePopupId: Number,
    livePopupLabel: String,
  },
  data() {
    return {
      // Only Label and the linked event's title are meaningful to search
      // by name; filterable: false on the rest keeps the search box from
      // matching noise like raw ids, ISO dates or click counts.
      headers: [
        { text: 'Label', value: 'label' },
        { text: 'Content', value: 'type' },
        { text: 'Active', value: 'status', filterable: false },
        { text: 'Window', value: 'window', filterable: false },
        { text: 'Stats', value: 'stats', filterable: false },
        { text: 'Actions', value: 'actions', sortable: false, filterable: false },
      ],
      dialogOpen: false,
      dialogType: 'add',
      saving: false,
      previewDevice: 'web',
      customPhotoFile: null,
      customPhotoPreviewUrl: null,
      form: this.emptyForm(),
      popupsList: this.popups,
      // Local reactive copies: refresh() needs to update these after
      // every save, not just on the initial page load.
      liveId: this.livePopupId,
      liveLabel: this.livePopupLabel,
      search: '',
      eventFilter: null,
    }
  },
  computed: {
    // Only the active events that actually have a popup linked to them —
    // not the full 200-event list, which would make this dropdown mostly
    // dead weight (picking an event with zero popups just shows an empty
    // table).
    activeEventFilterItems() {
      const seen = new Map()
      this.popupsList
        .filter((p) => p.type === 'event' && p.event_id)
        .forEach((p) => seen.set(p.event_id, p.event_title || `Event #${p.event_id}`))
      return Array.from(seen, ([id, label]) => ({ id, label }))
    },
    filteredPopups() {
      if (!this.eventFilter) return this.popupsList
      return this.popupsList.filter((p) => p.event_id === this.eventFilter)
    },
    // Only worth warning about when this popup would actually take over
    // as the one shown — i.e. it's about to be Active, some other popup
    // is currently live, and that other popup isn't this same one.
    showLiveWarning() {
      return !!this.liveId && this.form.status && this.liveId !== this.form.id
    },
    eventItems() {
      return this.events.map(e => ({
        id: e.id,
        label: e.title + (e.start_date ? ' — ' + e.start_date : ''),
      }))
    },
    selectedEvent() {
      if (this.form.type !== 'event' || !this.form.event_id) return null
      return this.events.find(e => e.id === this.form.event_id) || null
    },
    previewHeading() {
      return this.form.type === 'event' ? (this.selectedEvent ? this.selectedEvent.title : '') : this.form.custom_heading
    },
    previewBody() {
      return this.form.type === 'custom' ? this.form.custom_body : ''
    },
    previewDate() {
      return this.form.type === 'event' && this.selectedEvent ? this.selectedEvent.start_date : ''
    },
    previewLocation() {
      return this.form.type === 'event' && this.selectedEvent ? this.selectedEvent.location : ''
    },
    previewButtonText() {
      if (this.form.type === 'event') return 'View Event'
      return this.form.custom_button_text || 'Learn More'
    },
    previewImageSrc() {
      if (this.form.type === 'event') {
        return this.selectedEvent && this.selectedEvent.photo_path ? this.baseurl + '/img/' + this.selectedEvent.photo_path : null
      }
      // A freshly picked file previews from the browser; an existing
      // saved photo previews from the server.
      return this.customPhotoPreviewUrl || (this.form.custom_photo_path ? this.baseurl + '/img/' + this.form.custom_photo_path : null)
    },
    previewFrameStyle() {
      return this.previewDevice === 'mobile' ? { width: '375px' } : { width: '100%' }
    },
  },
  methods: {
    // "YYYY-MM-DDTHH:mm" — the exact format <input type=datetime-local>
    // expects, built from the browser's own local wall-clock time (which
    // the server takes literally as Irish time; see the field hint).
    toDatetimeLocal(date) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    // Active Window is required; default to "starting now, running a
    // week" so a new popup isn't blocked by a blank field the admin has
    // to think to fill in, and so a clone doesn't just reuse the
    // original's window untouched.
    defaultActiveWindow() {
      const from = new Date()
      const to = new Date(from.getTime() + 7 * 24 * 60 * 60 * 1000)
      return { from: this.toDatetimeLocal(from), to: this.toDatetimeLocal(to) }
    },
    emptyForm() {
      const window = this.defaultActiveWindow()
      return {
        id: null,
        label: '',
        type: 'event',
        event_id: null,
        custom_heading: '',
        custom_body: '',
        custom_photo_path: '',
        custom_button_text: '',
        custom_link_url: '',
        status: true,
        active_from: window.from,
        active_to: window.to,
      }
    },
    // The default per-cell filter would only match the raw 'event'/
    // 'custom' string, not the linked event's actual title people would
    // actually search by — so check both label and event_title directly
    // against the whole row, ignoring which column triggered the call.
    filterPopups(value, search, item) {
      if (!search) return true
      const needle = search.toString().toLowerCase()
      return (item.label || '').toLowerCase().includes(needle)
        || (item.event_title || '').toLowerCase().includes(needle)
    },
    formatDate(v) {
      if (!v) return ''
      return new Date(v).toLocaleString()
    },
    // "in 3d" / "3d ago" / "today" / "tomorrow" / "yesterday" — short,
    // scannable relative phrasing instead of two full timestamps.
    relativePhrase(target, now) {
      const diffDays = Math.round((target - now) / (1000 * 60 * 60 * 24))
      if (diffDays === 0) return 'today'
      if (diffDays === 1) return 'tomorrow'
      if (diffDays === -1) return 'yesterday'
      return diffDays > 0 ? `in ${diffDays}d` : `${Math.abs(diffDays)}d ago`
    },
    windowSummary(item) {
      if (!item.active_from && !item.active_to) return 'Always'
      const now = new Date()
      const from = item.active_from ? new Date(item.active_from) : null
      const to = item.active_to ? new Date(item.active_to) : null

      if (from && now < from) return 'Starts ' + this.relativePhrase(from, now)
      if (to && now > to) return 'Ended ' + this.relativePhrase(to, now)
      return to ? ('Live now, ends ' + this.relativePhrase(to, now)) : 'Live now'
    },
    // Exact times, shown on hover rather than taking up column width.
    windowTooltip(item) {
      if (!item.active_from && !item.active_to) return 'No start/end limit'
      return (this.formatDate(item.active_from) || 'no start limit') + ' \u2192 ' + (this.formatDate(item.active_to) || 'no end limit')
    },
    openAdd() {
      this.dialogType = 'add'
      this.form = this.emptyForm()
      this.customPhotoFile = null
      this.customPhotoPreviewUrl = null
      this.dialogOpen = true
    },
    openEdit(item) {
      this.dialogType = 'edit'
      this.customPhotoFile = null
      this.customPhotoPreviewUrl = null
      axios.get('/admin/popups/get/' + item.id).then((response) => {
        const p = response.data
        this.form = {
          id: p.id,
          label: p.label,
          type: p.type,
          event_id: p.event_id,
          custom_heading: p.custom_heading || '',
          custom_body: p.custom_body || '',
          custom_photo_path: p.custom_photo_path || '',
          custom_button_text: p.custom_button_text || '',
          custom_link_url: p.custom_link_url || '',
          status: !!p.status,
          active_from: p.active_from ? p.active_from.slice(0, 16) : '',
          active_to: p.active_to ? p.active_to.slice(0, 16) : '',
        }
        this.dialogOpen = true
      })
    },
    cloneItem(item) {
      this.dialogType = 'add'
      this.customPhotoFile = null
      this.customPhotoPreviewUrl = null
      axios.get('/admin/popups/get/' + item.id).then((response) => {
        const p = response.data
        const window = this.defaultActiveWindow()
        this.form = {
          id: null,
          label: p.label + ' (copy)',
          type: p.type,
          event_id: p.event_id,
          custom_heading: p.custom_heading || '',
          custom_body: p.custom_body || '',
          // Carried through as-is (no re-upload needed); the backend
          // reuses this stored path when no new file is attached.
          custom_photo_path: p.custom_photo_path || '',
          custom_button_text: p.custom_button_text || '',
          custom_link_url: p.custom_link_url || '',
          status: !!p.status,
          // Deliberately NOT copying the original's active window — a
          // clone exists specifically to run the same content on a
          // different schedule, so it always gets a fresh default.
          active_from: window.from,
          active_to: window.to,
        }
        this.dialogOpen = true
      })
    },
    onPhotoPicked(file) {
      this.customPhotoPreviewUrl = file ? URL.createObjectURL(file) : null
    },
    save() {
      this.saving = true
      const formData = new FormData()
      Object.keys(this.form).forEach((key) => {
        if (this.form[key] !== null) formData.append(key, this.form[key])
      })
      if (this.customPhotoFile) {
        formData.append('photo', this.customPhotoFile)
      }

      const url = this.dialogType === 'add' ? '/admin/popups/add' : '/admin/popups/update'
      // The endpoint returns a bare PHP `true`, which Laravel serialises as
      // the literal body "1" with Content-Type text/html — axios leaves it
      // as the string "1", not the boolean true, so this must be a loose
      // comparison (matching the same convention Events/AdminIndex.vue
      // uses for the same reason).
      axios.post(url, formData).then((response) => {
        this.saving = false
        if (response.data == true) {
          this.dialogOpen = false
          this.refresh()
        } else {
          alert('Save failed')
        }
      }).catch((error) => {
        this.saving = false
        // Surface the actual validation message (e.g. a missing Active
        // Window field) instead of a bare, unhelpful "Save failed".
        const errors = error.response && error.response.status === 422 && error.response.data.errors
        if (errors) {
          alert(Object.values(errors).flat().join('\n'))
        } else {
          alert('Save failed')
        }
      })
    },
    remove(item) {
      if (!confirm('Delete the popup "' + item.label + '"? This cannot be undone.')) return
      axios.post('/admin/popups/delete/' + item.id).then(() => this.refresh())
    },
    refresh() {
      axios.get('/admin/popups/list').then((response) => {
        this.popupsList = response.data.popups
        this.liveId = response.data.livePopupId
        this.liveLabel = response.data.livePopupLabel
      })
    },
  },
}
</script>
